import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PeepingLogo } from "@/components/PeepingLogo";
import { SandboxNotice, fmtMoney } from "@/components/Peep";

export default function Checkout() {
  const [params] = useSearchParams();
  const planId = params.get("plan");
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/memberships/plans").then((r) => setPlan(r.data.plans.find((p) => p.id === planId) || false)).catch(() => setPlan(false));
  }, [planId]);

  const start = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/pay/checkout", { plan_id: planId });
      navigate(data.hosted_url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setLoading(false);
    }
  };

  const q = plan?.quote;
  return (
    <div data-testid="checkout-screen" className="min-h-screen px-5 pt-6">
      <button onClick={() => navigate(-1)} className="text-[#a7a0af] hover:text-white" aria-label="Back"><ArrowLeft size={22} /></button>
      <div className="mt-6 flex flex-col items-center">
        <PeepingLogo className="w-36" />
        <p className="mt-3 text-xs uppercase tracking-[0.3em] text-[#a7a0af]">PEEPING PAY</p>
      </div>
      <SandboxNotice className="mt-6" />
      {plan === false && <p className="mt-6 text-sm text-[#a7a0af]">Membership not found.</p>}
      {plan && (
        <div className="mt-5 rounded-2xl glass p-5">
          <h2 data-testid="checkout-plan-name" className="text-lg font-bold">{plan.name}</h2>
          <p className="mt-1 text-xs text-[#a7a0af]">{plan.description}</p>
          <dl className="mt-4 flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-[#a7a0af]">Monthly price</dt><dd>{fmtMoney(plan.price)}</dd></div>
            {q?.proration_credit > 0 && <div className="flex justify-between"><dt className="text-[#a7a0af]">Upgrade credit</dt><dd>−{fmtMoney(q.proration_credit)}</dd></div>}
            <div className="flex justify-between border-t border-white/10 pt-2 font-bold"><dt>Due today</dt><dd data-testid="checkout-amount">{fmtMoney(q?.amount_due ?? plan.price)}</dd></div>
          </dl>
          <p className="mt-3 flex items-center gap-2 text-xs text-[#a7a0af]"><Lock size={13} /> Card details are entered only on the payment provider's secure hosted page — never in PEEPING.</p>
          {error && <p data-testid="checkout-error" className="mt-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
          {q?.can_purchase ? (
            <button onClick={start} disabled={loading} data-testid="checkout-continue" className="btn-brand mt-5 w-full rounded-2xl py-4 text-base font-semibold text-white">
              {loading ? "Opening secure payment…" : "Continue to secure payment"}
            </button>
          ) : (
            <p data-testid="checkout-unavailable" className="mt-5 text-center text-sm text-[#a7a0af]">{q?.reason}</p>
          )}
        </div>
      )}
    </div>
  );
}
